"use strict";
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.Client = void 0;
var bitwise_1 = __importDefault(require("bitwise"));
var superagent_1 = __importDefault(require("superagent"));
var bitcoin_1 = __importDefault(require("./bitcoin"));
var sha_1 = require("hash.js/lib/hash/sha");
var index_1 = require("./index");
var constants_1 = require("./constants");
var ethereum_1 = __importDefault(require("./ethereum"));
var genericSigning_1 = require("./genericSigning");
var util_1 = require("./util");
var EMPTY_WALLET_UID = Buffer.alloc(32);
/**
 * `Client` is a class-based interface for managing a Lattice device.
 */
var Client = /** @class */ (function () {
    /**
     * @param params - Parameters are passed as an object.
     */
    function Client(_a) {
        var baseUrl = _a.baseUrl, name = _a.name, privKey = _a.privKey, stateData = _a.stateData, timeout = _a.timeout, retryCount = _a.retryCount, skipRetryOnWrongWallet = _a.skipRetryOnWrongWallet;
        // Default state params
        // -----
        this.ephemeralPub = null;
        this.deviceId = null;
        this.isPaired = false;
        this.activeWallets = {
            internal: {
                uid: EMPTY_WALLET_UID,
                name: null,
                capabilities: null,
                external: false,
            },
            external: {
                uid: EMPTY_WALLET_UID,
                name: null,
                capabilities: null,
                external: true,
            },
        };
        this.skipRetryOnWrongWallet = skipRetryOnWrongWallet || false;
        // The user may pass in state data to rehydrate a session that was previously cached
        // -----
        if (stateData) {
            this._unpackAndApplyStateData(stateData);
            return;
        }
        // Other params to check if `stateData` is not included
        // -----
        // `baseUrl` describes where to send HTTP requests
        this.baseUrl = baseUrl || constants_1.BASE_URL;
        // `name` is a human readable string associated with this app on the Lattice
        if (name && (name.length < 5 || name.length > 24)) {
            throw new Error('`name` must be 5-24 characters');
        }
        this.name = name || 'Unknown';
        // `privKey` is used to generate a keypair, which is used for maintaining an encrypted messaging
        // channel with the target Lattice
        this.privKey = privKey || (0, util_1.randomBytes)(32);
        this.key = (0, util_1.getP256KeyPair)(this.privKey);
        // `retryCount` defines the number of automatic retries for asynchronous requests. Retries only
        // happen for certain device errors which indicate retrying is allowed.
        this.retryCount = retryCount || 3;
        // `timeout` is the number of milliseconds allowed before terminating asynchronous requests if
        // no response is returned in time
        this.timeout = timeout || 60000;
    }
    /**
     * Get a JSON string containing state data that can be used to rehydrate a session. Pass the
     * contents of this to the constructor as `stateData` to rehydrate.
     */
    Client.prototype.getStateData = function () {
        return this._packStateData();
    };
    /**
     * `getFwVersion` gets the firmware version of the paired device.
     * @returns Either an object with semver properties (fix, minor, and major) or `null`.
     */
    Client.prototype.getFwVersion = function () {
        if (this.fwVersion && this.fwVersion.length >= 3) {
            return {
                fix: this.fwVersion[0],
                minor: this.fwVersion[1],
                major: this.fwVersion[2],
            };
        }
        return null;
    };
    /**
     * `getAppName` returns the name of the application to which this device is currently paired.
     */
    Client.prototype.getAppName = function () {
        return this.name;
    };
    //=======================================================================
    // LATTICE FUNCTIONS
    //=======================================================================
    /**
     * `connect` will attempt to contact a device based on its deviceId. The response should include
     * an ephemeral public key, which is used to pair with the device in a later request.
     * @category Lattice
     */
    Client.prototype.connect = function (deviceId, _cb) {
        var _this = this;
        return new Promise(function (resolve, reject) {
            var cb = (0, util_1.promisifyCb)(resolve, reject, _cb);
            // User may "re-connect" if a device ID has previously been stored
            if (typeof deviceId === 'function') {
                if (!_this.deviceId)
                    return cb('No device ID has been stored. Please connect with your device ID first.');
                cb = deviceId;
            }
            else {
                // If the user passes in a device ID, connect to that device and save the new ID for future
                // use.
                _this.deviceId = deviceId;
            }
            var param = _this._buildRequest(constants_1.deviceCodes.CONNECT, _this.pubKeyBytes());
            _this._request(param, null, function (err, res) {
                if (err)
                    return cb(err);
                _this.isPaired = _this._handleConnect(res) || false;
                // If we are paired and are on older firmware (<0.14.1), we need a follow up request to sync
                // wallet state.
                if (_this.isPaired && !_this._fwVersionGTE(0, 14, 1)) {
                    _this.fetchActiveWallet(function (err) {
                        return cb(err, _this.isPaired);
                    });
                }
                else {
                    return cb(err, _this.isPaired);
                }
            });
        });
    };
    /**
     * If a pairing secret is provided, `pair` uses it to sign a hash of the public key, name, and
     * pairing secret. It then sends the name and signature to the device. If no pairing secret is
     * provided, `pair` sends a zero-length name buffer to the device.
     * @category Lattice
     * @returns The active wallet object.
     */
    Client.prototype.pair = function (pairingSecret, _cb) {
        var _this = this;
        return new Promise(function (resolve, reject) {
            var cb = (0, util_1.promisifyCb)(resolve, reject, _cb);
            // Build the secret hash from the salt
            var pubKey = _this.pubKeyBytes();
            var nameBuf = Buffer.alloc(25);
            if (_this.name.length < 5 || _this.name.length > 24) {
                return cb('Invalid length for name provided. Must be 5-24 characters.');
            }
            if (pairingSecret.length > 0) {
                // If a pairing secret of zero length is passed in, it usually indicates we want to cancel
                // the pairing attempt. In this case we pass a zero-length name buffer so the firmware can
                // know not to draw the error screen. Note that we still expect an error to come back
                // (RESP_ERR_PAIR_FAIL)
                nameBuf.write(_this.name);
            }
            var hash = (0, util_1.generateAppSecret)(pubKey, nameBuf, Buffer.from(pairingSecret));
            var sig = _this.key.sign(hash); // returns an array, not a buffer
            var derSig = (0, util_1.toPaddedDER)(sig);
            var payload = Buffer.concat([nameBuf, derSig]);
            // Build the request
            _this._request(payload, 'FINALIZE_PAIRING', function (err, res) {
                if (err)
                    return cb(err);
                // Recover the ephemeral key
                var errStr = _this._handlePair(res);
                if (errStr)
                    return cb(errStr);
                // Try to get the active wallet once pairing is successful
                _this.fetchActiveWallet(function (err) {
                    if (err)
                        return cb(err);
                    return cb(null, _this.hasActiveWallet());
                });
            });
        });
    };
    /**
     * `test` takes a data object with a testID and a payload, and sends them to the device.
     * @category Lattice
     */
    Client.prototype.test = function (data, _cb) {
        var _this = this;
        return new Promise(function (resolve, reject) {
            var cb = (0, util_1.promisifyCb)(resolve, reject, _cb);
            if (!data.payload)
                return cb('First argument must contain `testID` and `payload` fields.');
            var TEST_DATA_SZ = 500;
            var payload = Buffer.alloc(TEST_DATA_SZ + 6);
            payload.writeUInt32BE(data.testID, 0);
            payload.writeUInt16BE(data.payload.length, 4);
            data.payload.copy(payload, 6);
            _this._request(payload, 'TEST', function (err, res) {
                if (err)
                    return cb(err);
                var decrypted = _this._handleEncResponse(res, constants_1.decResLengths.test);
                if (decrypted.err !== null)
                    return cb(decrypted.err);
                return cb(null, decrypted.data.slice(65)); // remove ephem pub
            });
        });
    };
    /**
     * `getAddresses` takes a starting path and a number to get the addresses associated with the
     * active wallet.
     * @category Lattice
     * @returns An array of addresses.
     */
    Client.prototype.getAddresses = function (opts, _cb) {
        var _this = this;
        return new Promise(function (resolve, reject) {
            var cb = (0, util_1.promisifyCb)(resolve, reject, _cb);
            var MAX_ADDR = 10;
            var startPath = opts.startPath, n = opts.n, _a = opts.flag, flag = _a === void 0 ? 0 : _a;
            if (startPath === undefined || n === undefined)
                return cb('Please provide `startPath` and `n` options');
            if (startPath.length < 2 || startPath.length > 5)
                return cb('Path must include between 2 and 5 indices');
            if (!(0, util_1.isUInt4)(n) || !(0, util_1.isUInt4)(flag)) {
                return cb('Parameters `n` and `flag` must be integers between 0 and 15 inclusive');
            }
            if (n > MAX_ADDR)
                return cb("You may only request ".concat(MAX_ADDR, " addresses at once."));
            var fwConstants = (0, constants_1.getFwVersionConst)(_this.fwVersion);
            var flags = fwConstants.getAddressFlags || [];
            var isPubkeyOnly = flags.indexOf(flag) > -1 &&
                (flag === constants_1.EXTERNAL.GET_ADDR_FLAGS.ED25519_PUB ||
                    flag === constants_1.EXTERNAL.GET_ADDR_FLAGS.SECP256K1_PUB);
            if (!isPubkeyOnly && !(0, util_1.isValidAssetPath)(startPath, fwConstants)) {
                return cb('Parent derivation path is not supported');
            }
            var sz = 32 + 20 + 1; // walletUID + 5 u32 indices + count/flag
            if (fwConstants.varAddrPathSzAllowed) {
                sz += 1; // pathDepth
            }
            else if (startPath.length !== 5) {
                return cb('Your Lattice firmware only supports derivation paths with 5 indices. Please upgrade.');
            }
            var payload = Buffer.alloc(sz);
            var off = 0;
            // WalletUID
            var wallet = _this.getActiveWallet();
            if (wallet === null)
                return cb('No active wallet.');
            wallet.uid.copy(payload, off);
            off += 32;
            // Build the start path (5x u32 indices)
            if (fwConstants.varAddrPathSzAllowed) {
                payload.writeUInt8(startPath.length, off);
                off += 1;
            }
            for (var i = 0; i < 5; i++) {
                if (i <= startPath.length)
                    payload.writeUInt32BE(startPath[i], off);
                off += 4;
            }
            // Specify the number of subsequent addresses to request. We also allow the user to skip the
            // cache and request any address related to the asset in the wallet.
            var val, flagVal = 0;
            if (fwConstants.addrFlagsAllowed) {
                // A 4-bit flag can be used for non-standard address requests This needs to be combined with
                // `n` as a 4 bit value
                flagVal =
                    fwConstants.getAddressFlags &&
                        fwConstants.getAddressFlags.indexOf(flag) > -1
                        ? flag
                        : 0;
                var flagBits = bitwise_1.default.nibble.read(flagVal);
                var countBits = bitwise_1.default.nibble.read(n);
                val = bitwise_1.default.byte.write(flagBits.concat(countBits));
            }
            else {
                // Very old firmware does not support this flag. We can deprecate this soon.
                val = n;
            }
            payload.writeUInt8(val, off);
            off++;
            return _this._request(payload, 'GET_ADDRESSES', function (err, res) {
                if (err)
                    return cb(err);
                var parsedRes = _this._handleGetAddresses(res, flagVal);
                if (parsedRes.err)
                    return cb(parsedRes.err);
                return cb(null, parsedRes.data);
            });
        });
    };
    /**
     * `sign` builds and sends a request for signing to the device.
     * @category Lattice
     * @returns The response from the device.
     */
    Client.prototype.sign = function (opts, _cb, cachedData, nextCode) {
        var _this = this;
        if (cachedData === void 0) { cachedData = null; }
        if (nextCode === void 0) { nextCode = null; }
        return new Promise(function (resolve, reject) {
            var cb = (0, util_1.promisifyCb)(resolve, reject, _cb);
            var data = opts.data;
            if (!data) {
                return cb('You must provide `data`');
            }
            // All transaction requests must be put into the same sized buffer. This comes from
            // sizeof(GpTransactionRequest_t), but note we remove the 2-byte schemaId since it is not
            // returned from our resolver. Note that different firmware versions may have different data
            // sizes.
            var fwConstants = (0, constants_1.getFwVersionConst)(_this.fwVersion);
            // Build the signing request payload to send to the device. If we catch bad params, return an
            // error instead
            data = __assign({ fwConstants: fwConstants }, data);
            var req, reqPayload, schema;
            var currency = null;
            if (cachedData !== null && nextCode !== null) {
                currency = cachedData.currency;
                req = cachedData;
                reqPayload = Buffer.concat([nextCode, req.extraDataPayloads.shift()]);
                schema = constants_1.signingSchema.EXTRA_DATA;
            }
            else {
                currency = opts.currency;
                try {
                    // TEMPORARY BRIDGE -- DEPRECATE ME In v0.15.0 Lattice firmware removed the legacy ETH
                    // signing path, so we need to convert such requests to general signing requests using the
                    // EVM decoder. NOTE: Not every request can be converted, so users should switch to using
                    // general signing requests for newer firmware versions. EIP1559 and EIP155 legacy
                    // requests will convert, but others may not.
                    var useEVMLegacyConverter = fwConstants.genericSigning &&
                        fwConstants.genericSigning.encodingTypes &&
                        fwConstants.genericSigning.encodingTypes.EVM;
                    if (currency === 'ETH' && useEVMLegacyConverter) {
                        console.warn('Using the legacy ETH signing path. This will soon be deprecated. ' +
                            'Please switch to general signing request.');
                        var payload_1;
                        try {
                            payload_1 = ethereum_1.default.ethConvertLegacyToGenericReq(data);
                        }
                        catch (err) {
                            return cb('Could not convert legacy request. Please switch to a general signing ' +
                                'request. See gridplus-sdk docs for more information.');
                        }
                        data = {
                            fwConstants: fwConstants,
                            encodingType: constants_1.EXTERNAL.SIGNING.ENCODINGS.EVM,
                            curveType: constants_1.EXTERNAL.SIGNING.CURVES.SECP256K1,
                            hashType: constants_1.EXTERNAL.SIGNING.HASHES.KECCAK256,
                            signerPath: data.signerPath,
                            payload: payload_1,
                        };
                        currency = null;
                    }
                    if (currency === 'ETH') {
                        // Legacy signing pathway -- should deprecate in the future
                        req = ethereum_1.default.buildEthereumTxRequest(data);
                    }
                    else if (currency === 'ETH_MSG') {
                        req = ethereum_1.default.buildEthereumMsgRequest(data);
                    }
                    else if (currency === 'BTC') {
                        req = bitcoin_1.default.buildBitcoinTxRequest(data);
                    }
                    else {
                        req = (0, genericSigning_1.buildGenericSigningMsgRequest)(data);
                    }
                }
                catch (err) {
                    return cb("Error building signing request: ".concat(err.message));
                }
                if (req.err !== undefined)
                    return cb(req.err);
                if (req.payload.length > fwConstants.reqMaxDataSz)
                    return cb('Transaction is too large');
                reqPayload = req.payload;
                schema = req.schema;
            }
            // Build the payload
            var payload = Buffer.alloc(2 + fwConstants.reqMaxDataSz);
            var off = 0;
            // Whether there will be follow up requests
            var hasExtraPayloads = req.extraDataPayloads && Number(req.extraDataPayloads.length > 0);
            payload.writeUInt8(hasExtraPayloads, off);
            off += 1;
            // Copy request schema (e.g. ETH or BTC transfer)
            payload.writeUInt8(schema, off);
            off += 1;
            // Copy the wallet UID
            var wallet = _this.getActiveWallet();
            if (wallet === null)
                return cb('No active wallet.');
            wallet.uid.copy(payload, off);
            off += wallet.uid.length;
            // Build data based on the type of request Copy the payload of the request
            reqPayload.copy(payload, off);
            // Construct the encrypted request and send it
            return _this._request(payload, 'SIGN_TRANSACTION', function (err, res) {
                if (err) {
                    // If there was another error caught, return it
                    if (err)
                        return cb(err);
                }
                else if (hasExtraPayloads) {
                    var decrypted = _this._handleEncResponse(res, constants_1.decResLengths.sign);
                    nextCode = decrypted.data.slice(65, 73);
                    if (!cachedData) {
                        cachedData = req;
                        // We need to keep track of the currency to handler the decoding
                        cachedData.currency = currency;
                    }
                    return _this.sign(opts, cb, cachedData, nextCode);
                }
                else {
                    // Correct wallet and no errors -- handle the response
                    try {
                        var parsedRes = _this._handleSign(res, currency, req);
                        return cb(null, parsedRes);
                    }
                    catch (err) {
                        return cb(err.message);
                    }
                }
            });
        });
    };
    /**
     * `addDecoders` sends an RLP-encoded list of decoders to the Lattice. A "decoder" is a piece of
     * data that can be used to decode some data in the future. The best example of this is the ABI
     * defintion of a contract function. This definition is used to deserialize EVM calldata for
     * future requests that call the specified function (as determined by the function selector).
     *
     * NOTE: The CRUD API to manage calldata decoders is written, but is currently
     * compiled out of firmware to free up code space. For now we will leave
     * these functions commented out.
     * NOTE: You will need to re-enable `import { encode as rlpEncode } from 'rlp';`
     *
     * @deprecated
     * @category Lattice
     * @returns The decrypted response.
     */
    Client.prototype.addDecoders = function (opts, _cb) {
        return new Promise(function (resolve, reject) {
            var cb = (0, util_1.promisifyCb)(resolve, reject, _cb);
            // TODO: Update function comment if/when this is re-enabled.
            return cb('Feature currently disabled in Lattice firmware. Please include ' +
                'calldata decoder data in the signing request itself.');
            /*
            const { decoders, decoderType } = opts;
            const fwConstants = getFwVersionConst(this.fwVersion);
            if (!fwConstants.maxDecoderBufSz) {
              return cb('Please update Lattice firmware.');
            }
            const payload = Buffer.alloc(3 + fwConstants.maxDecoderBufSz);
            const encDecoders = Buffer.from(rlpEncode(decoders));
            if (encDecoders.length > fwConstants.maxDecoderBufSz) {
              return cb(
                'Too much data to make request. Please remove some decoders.',
              );
            }
            payload.writeUInt8(decoderType, 0);
            payload.writeUInt16LE(encDecoders.length, 1);
            encDecoders.copy(payload, 3);
            return this._request(
              payload,
              'ADD_DECODERS',
              (err, res, responseCode) => {
                if (responseCode && responseCode !== responseCodes.RESP_SUCCESS) {
                  return cb('Error making request.');
                } else if (err) {
                  return cb(err);
                }
                this._handleEncResponse(res, decResLengths.empty);
                return cb(null);
              },
            );
            */
        });
    };
    /**
     * `getDecoders` fetches a set of decoders saved on the target Lattice.
     *
     * NOTE: The CRUD API to manage calldata decoders is written, but is currently
     * compiled out of firmware to free up code space. For now we will leave
     * these functions commented out.
     *
     * @deprecated
     * @category Lattice
     * @returns The decrypted response.
     */
    Client.prototype.getDecoders = function (opts, _cb) {
        return new Promise(function (resolve, reject) {
            var cb = (0, util_1.promisifyCb)(resolve, reject, _cb);
            // TODO: Update function comment if/when this is re-enabled.
            return cb('Feature currently disabled in Lattice firmware. Please include ' +
                'calldata decoder data in the signing request itself.');
            /*
            const { n = 1, startIdx = 0, skipTotal = false, decoderType } = opts;
            const fwConstants = getFwVersionConst(this.fwVersion);
            if (!fwConstants.maxDecoderBufSz) {
              return cb('Please update Lattice firmware.');
            }
            if (n < 1) {
              return cb('Must request at least one record (n=0)');
            } else if (n < 0 || startIdx < 0) {
              return cb('Both `n` and `startIdx` must be >=0');
            }
            const payload = Buffer.alloc(10);
            payload.writeUInt8(decoderType, 0);
            payload.writeUInt8(skipTotal ? 1 : 0, 1);
            payload.writeUInt32LE(startIdx, 2);
            payload.writeUInt32LE(n, 6);
            return this._request(payload, 'GET_DECODERS', (err, res) => {
              if (err) {
                return cb(err);
              }
              // Correct wallet and no errors -- handle the response
              const d = this._handleEncResponse(res, decResLengths.getDecoders);
              if (d.err) {
                return cb(d.err);
              }
              // Decode the response
              const decoders = [];
              let off = 65; // Skip 65 byte pubkey prefix
              const numFetched = d.data.readUInt32LE(off);
              off += 4;
              const total = d.data.readUInt32LE(off);
              off += 4;
              for (let i = 0; i < numFetched; i++) {
                const sz = d.data.readUInt32LE(off);
                off += 4;
                decoders.push(d.data.slice(off, off + sz));
                off += sz;
              }
              return cb(null, { decoders, total });
            });
            */
        });
    };
    /**
     * `removeDecoders` requests removal of a set of decoders on the target Lattice.
     *
     * NOTE: The CRUD API to manage calldata decoders is written, but is currently
     * compiled out of firmware to free up code space. For now we will leave
     * these functions commented out.
     * NOTE: You will need to re-enable `import { encode as rlpEncode } from 'rlp';`
     *
     * @deprecated
     * @category Lattice
     * @returns The decrypted response.
     */
    Client.prototype.removeDecoders = function (opts, _cb) {
        return new Promise(function (resolve, reject) {
            var cb = (0, util_1.promisifyCb)(resolve, reject, _cb);
            // TODO: Update function comment if/when this is re-enabled.
            return cb('Feature currently disabled in Lattice firmware. Please include ' +
                'calldata decoder data in the signing request itself.');
            /*
            const { decoders, decoderType, rmAll = false } = opts;
            const fwConstants = getFwVersionConst(this.fwVersion);
            if (!fwConstants.maxDecoderBufSz) {
              return cb('Please update Lattice firmware.');
            } else if ((!decoders || !decoders.length) && !rmAll) {
              return cb(
                'At least one decoder must be provided unless using `rmAll`.',
              );
            }
            const payload = Buffer.alloc(3 + fwConstants.maxDecoderBufSz);
            let encDecoders = Buffer.alloc(0);
            if (decoders) {
              encDecoders = Buffer.from(rlpEncode(decoders));
              if (encDecoders.length > fwConstants.maxDecoderBufSz) {
                return cb(
                  'Too much data to make request. Please remove some decoders.',
                );
              }
            }
            const sz = rmAll ? 0 : encDecoders.length;
            payload.writeUInt8(decoderType, 0);
            payload.writeUInt16LE(sz, 1);
            encDecoders.copy(payload, 3);
            return this._request(
              payload,
              'REMOVE_DECODERS',
              (err, res, responseCode) => {
                if (responseCode && responseCode !== responseCodes.RESP_SUCCESS) {
                  return cb('Error making request.');
                } else if (err) {
                  return cb(err);
                }
                const d = this._handleEncResponse(res, decResLengths.removeDecoders);
                if (d.err) {
                  return cb(d.err);
                }
                // Decode the response
                let off = 65; // Skip 65 byte pubkey prefix
                const numRemoved = d.data.readUInt32LE(off);
                off += 4;
                return cb(null, numRemoved);
              },
            );
            */
        });
    };
    /**
     * `addPermissionV0` takes in a currency, time window, spending limit, and decimals, and builds a
     * payload to send to the Lattice.
     *
     * NOTE: This feature has been deprecated, but may be replaced in the future.
     *
     * @deprecated
     * @category Lattice
     */
    Client.prototype.addPermissionV0 = function (opts, _cb) {
        return new Promise(function (resolve, reject) {
            var cb = (0, util_1.promisifyCb)(resolve, reject, _cb);
            return cb('This feature has been deprecated and may be replaced at a later time.');
            /*
            const { currency, timeWindow, limit, decimals, asset } = opts;
            if (
              !currency ||
              timeWindow === undefined ||
              limit === undefined ||
              decimals === undefined ||
              timeWindow === null ||
              limit === null ||
              decimals === null
            )
              return cb(
                'currency, timeWindow, decimals, and limit are all required options.',
              );
            else if (timeWindow === 0 || limit === 0)
              return cb('Time window and spending limit must be positive.');
            // Build the name of the permission
            let name = currency;
            if (asset) name += `_${asset}`;
            // Start building the payload
            const payload = Buffer.alloc(293);
            // Copy the name
            if (Buffer.from(name).length > 255) return cb('Asset name too long.');
            Buffer.from(name).copy(payload, 0);
            // Convert the limit to a 32 byte hex buffer and copy it in
            const limitBuf = ethereum.ensureHexBuffer(limit);
            if (limitBuf.length > 32) return cb('Limit too large.');
            limitBuf.copy(payload, 256 + (32 - limitBuf.length));
            // Copy the time window (seconds)
            payload.writeUInt32BE(timeWindow, 288);
            payload.writeUInt8(decimals, 292);
            // Encrypt the request and send it to the Lattice.
            return this._request(payload, 'ADD_PERMISSION_V0', (err, res) => {
              if (err) {
                // If there was another error caught, return it
                if (err) return cb(err);
              } else {
                // Correct wallet and no errors -- handle the response
                const d = this._handleEncResponse(res, decResLengths.empty);
                if (d.err) return cb(d.err);
                return cb(null);
              }
            });
            */
        });
    };
    /**
     * `getKvRecords` fetches a list of key-value records from the Lattice.
     * @category Lattice
     */
    Client.prototype.getKvRecords = function (opts, _cb) {
        var _this = this;
        return new Promise(function (resolve, reject) {
            var cb = (0, util_1.promisifyCb)(resolve, reject, _cb);
            var _a = opts.type, type = _a === void 0 ? 0 : _a, _b = opts.n, n = _b === void 0 ? 1 : _b, _c = opts.start, start = _c === void 0 ? 0 : _c;
            var fwConstants = (0, constants_1.getFwVersionConst)(_this.fwVersion);
            if (!fwConstants.kvActionsAllowed) {
                return cb('Unsupported. Please update firmware.');
            }
            else if (n < 1) {
                return cb('You must request at least one record.');
            }
            else if (n > fwConstants.kvActionMaxNum) {
                return cb("You may only request up to ".concat(fwConstants.kvActionMaxNum, " records at once."));
            }
            var payload = Buffer.alloc(9);
            payload.writeUInt32LE(type, 0);
            payload.writeUInt8(n, 4);
            payload.writeUInt32LE(start, 5);
            // Encrypt the request and send it to the Lattice.
            return _this._request(payload, 'GET_KV_RECORDS', function (err, res) {
                if (err) {
                    // If there was another error caught, return it
                    if (err)
                        return cb(err);
                }
                else {
                    // Correct wallet and no errors -- handle the response
                    var d = _this._handleEncResponse(res, constants_1.decResLengths.getKvRecords);
                    if (d.err)
                        return cb(d.err);
                    // Decode the response
                    var off = 65; // Skip 65 byte pubkey prefix
                    var nTotal = parseInt(d.data.slice(off, off + 4).toString('hex'), 16);
                    off += 4;
                    var nFetched = parseInt(d.data.slice(off, off + 1).toString('hex'), 16);
                    off += 1;
                    if (nFetched > fwConstants.kvActionMaxNum)
                        return cb('Too many records fetched. Firmware error.');
                    var records = [];
                    for (var i = 0; i < nFetched; i++) {
                        var r = {};
                        r.id = parseInt(d.data.slice(off, off + 4).toString('hex'), 16);
                        off += 4;
                        r.type = parseInt(d.data.slice(off, off + 4).toString('hex'), 16);
                        off += 4;
                        r.caseSensitive =
                            parseInt(d.data.slice(off, off + 1).toString('hex'), 16) === 1
                                ? true
                                : false;
                        off += 1;
                        var keySz = parseInt(d.data.slice(off, off + 1).toString('hex'), 16);
                        off += 1;
                        r.key = d.data.slice(off, off + keySz - 1).toString();
                        off += fwConstants.kvKeyMaxStrSz + 1;
                        var valSz = parseInt(d.data.slice(off, off + 1).toString('hex'), 16);
                        off += 1;
                        r.val = d.data.slice(off, off + valSz - 1).toString();
                        off += fwConstants.kvValMaxStrSz + 1;
                        records.push(r);
                    }
                    return cb(null, {
                        records: records,
                        total: nTotal,
                        fetched: nFetched,
                    });
                }
            });
        });
    };
    /**
     * `addKvRecords` takes in a set of key-value records and sends a request to add them to the
     * Lattice.
     * @category Lattice
     * @returns A callback with an error or null.
     */
    Client.prototype.addKvRecords = function (opts, _cb) {
        var _this = this;
        return new Promise(function (resolve, reject) {
            var cb = (0, util_1.promisifyCb)(resolve, reject, _cb);
            var _a = opts.type, type = _a === void 0 ? 0 : _a, _b = opts.records, records = _b === void 0 ? {} : _b, _c = opts.caseSensitive, caseSensitive = _c === void 0 ? false : _c;
            var fwConstants = (0, constants_1.getFwVersionConst)(_this.fwVersion);
            if (!fwConstants.kvActionsAllowed) {
                return cb('Unsupported. Please update firmware.');
            }
            else if (typeof records !== 'object' ||
                Object.keys(records).length === 0) {
                return cb('One or more key-value mapping must be provided in `records` param.');
            }
            else if (Object.keys(records).length > fwConstants.kvActionMaxNum) {
                return cb("Too many keys provided. Please only provide up to ".concat(fwConstants.kvActionMaxNum, "."));
            }
            else if (Object.keys(records).length < 1) {
                return cb('You must provide at least one key to add.');
            }
            var payload = Buffer.alloc(1 + 139 * fwConstants.kvActionMaxNum);
            payload.writeUInt8(Object.keys(records).length, 0);
            var off = 1;
            try {
                Object.keys(records).forEach(function (key) {
                    if (typeof key !== 'string' ||
                        String(key).length > fwConstants.kvKeyMaxStrSz) {
                        throw new Error("Key ".concat(key, " too large. Must be <=").concat(fwConstants.kvKeyMaxStrSz, " characters."));
                    }
                    else if (typeof records[key] !== 'string' ||
                        String(records[key]).length > fwConstants.kvValMaxStrSz) {
                        throw new Error("Value ".concat(records[key], " too large. Must be <=").concat(fwConstants.kvValMaxStrSz, " characters."));
                    }
                    else if (String(key).length === 0 ||
                        String(records[key]).length === 0) {
                        throw new Error('Keys and values must be >0 characters.');
                    }
                    else if (!constants_1.ASCII_REGEX.test(key) ||
                        !constants_1.ASCII_REGEX.test(records[key])) {
                        throw new Error('Unicode characters are not supported.');
                    }
                    // Skip the ID portion. This will get added by firmware.
                    payload.writeUInt32LE(0, off);
                    off += 4;
                    payload.writeUInt32LE(type, off);
                    off += 4;
                    payload.writeUInt8(caseSensitive ? 1 : 0, off);
                    off += 1;
                    payload.writeUInt8(String(key).length + 1, off);
                    off += 1;
                    Buffer.from(String(key)).copy(payload, off);
                    off += fwConstants.kvKeyMaxStrSz + 1;
                    payload.writeUInt8(String(records[key]).length + 1, off);
                    off += 1;
                    Buffer.from(String(records[key])).copy(payload, off);
                    off += fwConstants.kvValMaxStrSz + 1;
                });
            }
            catch (err) {
                return cb("Error building request: ".concat(err.message));
            }
            // Encrypt the request and send it to the Lattice.
            return _this._request(payload, 'ADD_KV_RECORDS', function (err, res) {
                if (err) {
                    // If there was another error caught, return it
                    if (err)
                        return cb(err);
                }
                else {
                    // Correct wallet and no errors -- handle the response
                    var d = _this._handleEncResponse(res, constants_1.decResLengths.empty);
                    if (d.err)
                        return cb(d.err);
                    return cb(null);
                }
            });
        });
    };
    /**
     * `removeKvRecords` takes in an array of ids and sends a request to remove them from the Lattice.
     * @category Lattice
     * @returns A callback with an error or null.
     */
    Client.prototype.removeKvRecords = function (opts, _cb) {
        var _this = this;
        return new Promise(function (resolve, reject) {
            var cb = (0, util_1.promisifyCb)(resolve, reject, _cb);
            var _a = opts.type, type = _a === void 0 ? 0 : _a, _b = opts.ids, ids = _b === void 0 ? [] : _b;
            var fwConstants = (0, constants_1.getFwVersionConst)(_this.fwVersion);
            if (!fwConstants.kvActionsAllowed) {
                return cb('Unsupported. Please update firmware.');
            }
            else if (!Array.isArray(ids) || ids.length < 1) {
                return cb('You must include one or more `ids` to removed.');
            }
            else if (ids.length > fwConstants.kvRemoveMaxNum) {
                return cb("Only up to ".concat(fwConstants.kvRemoveMaxNum, " records may be removed at once."));
            }
            var payload = Buffer.alloc(5 + 4 * fwConstants.kvRemoveMaxNum);
            payload.writeUInt32LE(type, 0);
            payload.writeUInt8(ids.length, 4);
            for (var i = 0; i < ids.length; i++) {
                payload.writeUInt32LE(ids[i], 5 + 4 * i);
            }
            // Encrypt the request and send it to the Lattice.
            return _this._request(payload, 'REMOVE_KV_RECORDS', function (err, res) {
                if (err) {
                    // If there was another error caught, return it
                    if (err)
                        return cb(err);
                }
                else {
                    // Correct wallet and no errors -- handle the response
                    var d = _this._handleEncResponse(res, constants_1.decResLengths.empty);
                    if (d.err)
                        return cb(d.err);
                    return cb(null);
                }
            });
        });
    };
    /**
     * Fetch the active wallet in the device.
     * @returns callback with an error or null
     */
    Client.prototype.fetchActiveWallet = function (_cb) {
        var _this = this;
        return new Promise(function (resolve, reject) {
            var cb = (0, util_1.promisifyCb)(resolve, reject, _cb);
            var payload = Buffer.alloc(0);
            return _this._request(payload, 'GET_WALLETS', function (err, res) {
                if (err) {
                    _this._resetActiveWallets();
                    return cb(err);
                }
                return cb(_this._handleGetWallets(res));
            });
        });
    };
    //=======================================================================
    // INTERNAL FUNCTIONS These handle the logic around building requests and consuming responses.
    // They take into account the Lattice's serialization scheme among other protocols.
    //=======================================================================
    /**
     * Get the shared secret, derived via ECDH from the local private key and the ephemeral public key
     * @internal
     * @returns Buffer
     */
    Client.prototype._getSharedSecret = function () {
        // Once every ~256 attempts, we will get a key that starts with a `00` byte, which can lead to
        // problems initializing AES if we don't force a 32 byte BE buffer.
        return Buffer.from(this.key.derive(this.ephemeralPub.getPublic()).toArray('be', 32));
    };
    /**
     * Get the ephemeral id, which is the first 4 bytes of the shared secret generated from the local
     * private key and the ephemeral public key from the device.
     * @internal
     * @returns Buffer
     */
    Client.prototype._getEphemId = function () {
        if (this.ephemeralPub === null)
            return null;
        // EphemId is the first 4 bytes of the hash of the shared secret
        var secret = this._getSharedSecret();
        var hash = Buffer.from((0, sha_1.sha256)().update(secret).digest('hex'), 'hex');
        return hash.slice(0, 4);
    };
    /**
     * Builds an encrypted request
     * @internal
     */
    Client.prototype._buildEncRequest = function (enc_request_code, payload) {
        // Get the ephemeral id - all encrypted requests require there to be an epehemeral public key in
        // order to send
        var ephemId = parseInt(this._getEphemId().toString('hex'), 16);
        // Build the payload and checksum
        var payloadPreCs = Buffer.concat([
            Buffer.from([enc_request_code]),
            payload,
        ]);
        var cs = (0, util_1.checksum)(payloadPreCs);
        var payloadBuf = Buffer.alloc(payloadPreCs.length + 4);
        // Lattice validates checksums in little endian
        payloadPreCs.copy(payloadBuf, 0);
        payloadBuf.writeUInt32LE(cs, payloadPreCs.length);
        // Encrypt this payload
        var secret = this._getSharedSecret();
        var newEncPayload = (0, util_1.aes256_encrypt)(payloadBuf, secret);
        // Write to the overall payload. We must use the same length for every encrypted request and
        // must include a 32-bit ephemId along with the encrypted data
        var newPayload = Buffer.alloc(constants_1.ENC_MSG_LEN + 4);
        // First 4 bytes are the ephemeral id (in little endian)
        newPayload.writeUInt32LE(ephemId, 0);
        // Next N bytes
        newEncPayload.copy(newPayload, 4);
        return this._buildRequest(constants_1.deviceCodes.ENCRYPTED_REQUEST, newPayload);
    };
    /**
     * Build a request to send to the device.
     * @internal
     * @param request_code {uint8} - 8-bit unsigned integer representing the message request code
     * @param id {buffer} - 4 byte identifier (comes from HSM for subsequent encrypted reqs)
     * @param payload {buffer} - serialized payload
     * @returns {buffer}
     */
    Client.prototype._buildRequest = function (request_code, payload) {
        // Length of payload; we add 1 to the payload length to account for the request_code byte
        var L = payload && Buffer.isBuffer(payload) ? payload.length + 1 : 1;
        if (request_code === constants_1.deviceCodes.ENCRYPTED_REQUEST) {
            L = 1 + payload.length;
        }
        var i = 0;
        var preReq = Buffer.alloc(L + 8);
        // Build the header
        i = preReq.writeUInt8(constants_1.VERSION_BYTE, i);
        i = preReq.writeUInt8(constants_1.REQUEST_TYPE_BYTE, i);
        var id = (0, util_1.randomBytes)(4);
        i = preReq.writeUInt32BE(parseInt("0x".concat(id.toString('hex'))), i);
        i = preReq.writeUInt16BE(L, i);
        // Build the payload
        i = preReq.writeUInt8(request_code, i);
        if (L > 1)
            i = payload.copy(preReq, i);
        // Add the checksum
        var cs = (0, util_1.checksum)(preReq);
        var req = Buffer.alloc(preReq.length + 4); // 4-byte checksum
        i = preReq.copy(req);
        req.writeUInt32BE(cs, i);
        return req;
    };
    /**
     * Send a request to the device and wait for a response.
     * @internal
     * @returns The response code.
     */
    Client.prototype._request = function (payload, encReqCode, cb, retryCount) {
        var _this = this;
        if (retryCount === void 0) { retryCount = this.retryCount; }
        if (!this.deviceId) {
            return cb('Device ID is not set. Please set it and try again.');
        }
        else if (encReqCode && constants_1.encReqCodes[encReqCode] === undefined) {
            return cb('Unknown encrypted request code.');
        }
        // Encrypt the data if appropriate. Most requests are end-to-end encrypted, but some (e.g.
        // CONNNECT) are not encrypted.
        var data = encReqCode
            ? this._buildEncRequest(constants_1.encReqCodes[encReqCode], payload)
            : payload;
        var url = "".concat(this.baseUrl, "/").concat(this.deviceId);
        superagent_1.default
            .post(url)
            .timeout(this.timeout)
            .send({ data: data })
            .then(function (res) {
            // Handle formatting or generic HTTP errors
            if (!res || !res.body) {
                return cb("Invalid response: ".concat(res));
            }
            else if (res.body.status !== 200) {
                return cb("Error code ".concat(res.body.status, ": ").concat(res.body.message));
            }
            // Handle retry logic
            var parsed = (0, util_1.parseLattice1Response)(res.body.message);
            var deviceBusy = parsed.responseCode === constants_1.responseCodes.RESP_ERR_DEV_BUSY ||
                parsed.responseCode === constants_1.responseCodes.RESP_ERR_GCE_TIMEOUT;
            var wrongWallet = parsed.responseCode === constants_1.responseCodes.RESP_ERR_WRONG_WALLET;
            var invalidEphemId = parsed.responseCode === constants_1.responseCodes.RESP_ERR_INVALID_EPHEM_ID;
            var canRetry = retryCount > 0;
            // Re-connect and/or retry request if needed
            if (canRetry && deviceBusy) {
                // Wait a few seconds and retry
                setTimeout(function () {
                    _this._request(payload, encReqCode, cb, retryCount - 1);
                }, 3000);
            }
            else if (canRetry && invalidEphemId) {
                // Encrypted channel got out of sync. Reconnect and retry.
                _this.connect(_this.deviceId, function (err, isPaired) {
                    if (err) {
                        // Abort on connection error
                        return cb(err);
                    }
                    else if (!isPaired) {
                        // Abort if we are not paired
                        return cb('Not paired to device.');
                    }
                    _this._request(payload, encReqCode, cb, retryCount - 1);
                });
            }
            else if (canRetry && wrongWallet && !_this.skipRetryOnWrongWallet) {
                // Incorrect wallet being requested. Clear wallet state.
                _this._resetActiveWallets();
                // Refetch the active wallet.
                _this.fetchActiveWallet()
                    .then(function () {
                    payload = _this._replaceWalletUID(encReqCode, payload);
                    if (!payload) {
                        // Not allowed to retry. Exit here.
                        return cb('Wrong wallet. Failed to switch. Please reconnect.');
                    }
                    _this._request(payload, encReqCode, cb, 0);
                })
                    .catch(function (err) {
                    return cb(err);
                });
            }
            else if (parsed.responseCode || parsed.err) {
                if (parsed.err) {
                    return cb(parsed.err);
                }
                cb(constants_1.responseMsgs[parsed.responseCode] || 'Unknown request failure');
            }
            else {
                // All good
                cb(null, parsed.data, parsed.responseCode);
            }
        })
            .catch(function (err) {
            var isTimeout = err.code === 'ECONNABORTED' && err.errno === 'ETIME';
            if (isTimeout)
                return cb('Timeout waiting for device. Please ensure it is connected to the internet and try again in a minute.');
            else
                return cb("Failed to make request to device: ".concat(err.message));
        });
    };
    // ----- Device response -----
    /**
     * `_handleConnect` will call `StartPairingMode` on the device, which gives the user 60 seconds to
     * finalize the pairing. This will return an ephemeral public key, which is needed for the next
     * request.
     * - If the device is already paired, this ephemPub is simply used to encrypt the next request.
     * - If the device is not paired, it is needed to pair the device within 60 seconds.
     * @category Device Response
     * @internal
     * @returns true if we are paired to the device already
     */
    Client.prototype._handleConnect = function (res) {
        var off = 0;
        var isPaired = res.readUInt8(off) === constants_1.messageConstants.PAIRED;
        off++;
        // If we are already paired, we get the next ephemeral key
        var pub = res.slice(off, off + 65).toString('hex');
        off += 65;
        // Grab the firmware version (will be 0-length for older fw versions) It is of format
        // |fix|minor|major|reserved|
        this.fwVersion = res.slice(off, off + 4);
        off += 4;
        // Set the public key
        this.ephemeralPub = (0, util_1.getP256KeyPairFromPub)(pub);
        // If we are already paired, the response will include some encrypted data about the current
        // wallets This data was added in Lattice firmware v0.14.1
        if (isPaired && this._fwVersionGTE(0, 14, 1)) {
            // Later versions of firmware added wallet info
            var encWalletData = res.slice(off, off + 160);
            off += 160;
            var sharedSecret = this._getSharedSecret();
            var decWalletData = (0, util_1.aes256_decrypt)(encWalletData, sharedSecret);
            // Sanity check to make sure the last part of the decrypted data is empty. The last 2 bytes
            // are AES padding
            if (decWalletData[decWalletData.length - 2] !== 0 ||
                decWalletData[decWalletData.length - 1] !== 0) {
                throw new Error('Failed to connect to Lattice.');
            }
            this._parseAndAddWallets(decWalletData);
        }
        // return the state of our pairing
        return isPaired;
    };
    /**
     * All encrypted responses must be decrypted with the previous shared secret. Per specification,
     * decrypted responses will all contain a 65-byte public key as the prefix, which becomes the new
     * `ephemeralPub`.
     * @category Device Response
     * @internal
     */
    Client.prototype._handleEncResponse = function (encRes, len) {
        // Decrypt response
        var secret = this._getSharedSecret();
        var encData = encRes.slice(0, constants_1.ENC_MSG_LEN);
        var res = (0, util_1.aes256_decrypt)(encData, secret);
        // len does not include a 65-byte pubkey that prefies each encResponse
        len += 65;
        // Validate checksum. It will be the last 4 bytes of the decrypted payload. The length of the
        // decrypted payload will be fixed for each given message type.
        var toCheck = res.slice(0, len);
        var cs = parseInt("0x".concat(res.slice(len, len + 4).toString('hex')));
        var csCheck = (0, util_1.checksum)(toCheck);
        if (cs !== csCheck)
            return {
                err: "Checksum mismatch in response from Lattice (calculated ".concat(csCheck, ", wanted ").concat(cs, ")"),
            };
        // First 65 bytes is the next ephemeral pubkey
        var pub = res.slice(0, 65).toString('hex');
        try {
            this.ephemeralPub = (0, util_1.getP256KeyPairFromPub)(pub);
            return { err: null, data: res };
        }
        catch (e) {
            return {
                err: "Error handling getAddresses response: ".concat(e.toString()),
            };
        }
    };
    /**
     * Pair will create a new pairing if the user successfully enters the secret into the device in
     * time. If successful (`status=0`), the device will return a new ephemeral public key, which is
     * used to derive a shared secret for the next request
     * @category Device Response
     * @internal
     * @returns error (or null)
     */
    Client.prototype._handlePair = function (encRes) {
        var d = this._handleEncResponse(encRes, constants_1.decResLengths.empty);
        if (d.err)
            return d.err;
        this.isPaired = true;
        return null;
    };
    /**
     * @category Device Response
     * @internal
     * @return an array of address strings
     */
    Client.prototype._handleGetAddresses = function (encRes, flag) {
        // Handle the encrypted response
        var decrypted = this._handleEncResponse(encRes, constants_1.decResLengths.getAddresses);
        if (decrypted.err !== null)
            return decrypted;
        var addrData = decrypted.data;
        var off = 65; // Skip 65 byte pubkey prefix
        // Look for addresses until we reach the end (a 4 byte checksum)
        var addrs = [];
        // Pubkeys are formatted differently in the response
        var _a = index_1.Constants.GET_ADDR_FLAGS, ED25519_PUB = _a.ED25519_PUB, SECP256K1_PUB = _a.SECP256K1_PUB;
        var arePubkeys = flag === ED25519_PUB || flag === SECP256K1_PUB;
        if (arePubkeys) {
            off += 1; // skip uint8 representing pubkey type
        }
        while (off + 4 < constants_1.decResLengths.getAddresses) {
            if (arePubkeys) {
                // Pubkeys are shorter and are returned as buffers
                var pubBytes = addrData.slice(off, off + 65);
                var isEmpty = pubBytes.every(function (byte) { return byte === 0x00; });
                if (!isEmpty && flag === ED25519_PUB) {
                    // ED25519 pubkeys are 32 bytes
                    addrs.push(pubBytes.slice(0, 32));
                }
                else if (!isEmpty) {
                    // Only other returned pubkeys are ECC, or 65 bytes Note that we return full
                    // (uncompressed) ECC pubkeys
                    addrs.push(pubBytes);
                }
                off += 65;
            }
            else {
                // Otherwise we are dealing with address strings
                var addrBytes = addrData.slice(off, off + constants_1.ADDR_STR_LEN);
                off += constants_1.ADDR_STR_LEN;
                // Return the UTF-8 representation
                var len = addrBytes.indexOf(0); // First 0 is the null terminator
                if (len > 0) {
                    addrs.push(addrBytes.slice(0, len).toString());
                }
            }
        }
        return { data: addrs, err: null };
    };
    /**
     * If there is an active wallet, return null. Otherwise, return an error message.
     * @category Device Response
     * @internal
     * @param encRes - The encrypted response from the device.
     */
    Client.prototype._handleGetWallets = function (encRes) {
        var decrypted = this._handleEncResponse(encRes, constants_1.decResLengths.getWallets);
        if (decrypted.err !== null) {
            return decrypted;
        }
        var res = decrypted.data;
        // Skip 65byte pubkey prefix. WalletDescriptor contains 32byte id + 4byte flag + 35byte name
        this._parseAndAddWallets(res.slice(65));
        if (this.activeWallets.internal.uid.equals(EMPTY_WALLET_UID) &&
            this.activeWallets.external.uid.equals(EMPTY_WALLET_UID)) {
            return 'No active wallet.';
        }
        return null;
    };
    // Given a set of wallet data, which contains two wallet descriptors, parse the data and save it
    // to memory
    Client.prototype._parseAndAddWallets = function (walletData) {
        // Read the external wallet data first. If it is non-null, the external wallet will be the
        // active wallet of the device and we should save it. If the external wallet is blank, it means
        // there is no card present and we should save and use the interal wallet. If both wallets are
        // empty, it means the device still needs to be set up.
        var walletDescriptorLen = 71;
        // Internal first
        var off = 0;
        this.activeWallets.internal.uid = walletData.slice(off, off + 32);
        this.activeWallets.internal.capabilities = walletData.readUInt32BE(off + 32);
        this.activeWallets.internal.name = walletData.slice(off + 36, off + walletDescriptorLen);
        // Offset the first item
        off += walletDescriptorLen;
        // External
        this.activeWallets.external.uid = walletData.slice(off, off + 32);
        this.activeWallets.external.capabilities = walletData.readUInt32BE(off + 32);
        this.activeWallets.external.name = walletData.slice(off + 36, off + walletDescriptorLen);
    };
    /**
     * `_handleSign` takes the encrypted response from the device and decrypts it. It then parses the
     * decrypted response and returns the data.
     * @category Device Response
     * @internal
     * @param encRes - The encrypted response from the server
     * @param currencyType - The type of currency being signed.
     * @param req - The original request data
     * @returns The transaction data, the transaction hash, and the signature.
     */
    Client.prototype._handleSign = function (encRes, currencyType, req) {
        if (req === void 0) { req = null; }
        // Handle the encrypted response
        var decrypted = this._handleEncResponse(encRes, constants_1.decResLengths.sign);
        if (decrypted.err !== null)
            return { err: decrypted.err };
        var PUBKEY_PREFIX_LEN = 65;
        var PKH_PREFIX_LEN = 20;
        var off = PUBKEY_PREFIX_LEN; // Skip past pubkey prefix
        var res = decrypted.data;
        // Get the change data if we are making a BTC transaction
        var changeRecipient;
        if (currencyType === 'BTC') {
            var changeVersion = bitcoin_1.default.getAddressFormat(req.origData.changePath);
            var changePubkeyhash = res.slice(off, off + PKH_PREFIX_LEN);
            off += PKH_PREFIX_LEN;
            changeRecipient = bitcoin_1.default.getBitcoinAddress(changePubkeyhash, changeVersion);
        }
        var DERLength = 74; // max size of a DER signature -- all Lattice sigs are this long
        var SIGS_OFFSET = 10 * DERLength; // 10 signature slots precede 10 pubkey slots
        var PUBKEYS_OFFSET = PUBKEY_PREFIX_LEN + PKH_PREFIX_LEN + SIGS_OFFSET;
        if (currencyType === 'BTC') {
            var compressedPubLength = 33; // Size of compressed public key
            var pubkeys = [];
            var sigs = [];
            var n = 0;
            // Parse the signature for each output -- they are returned in the serialized payload in form
            // [pubkey, sig] There is one signature per output
            while (off < res.length) {
                // Exit out if we have seen all the returned sigs and pubkeys
                if (res[off] !== 0x30)
                    break;
                // Otherwise grab another set Note that all DER sigs returned fill the maximum 74 byte
                // buffer, but also contain a length at off+1, which we use to parse the non-zero data.
                // First get the signature from its slot
                var sigStart = off;
                var sigEnd = off + 2 + res[off + 1];
                sigs.push(res.slice(sigStart, sigEnd));
                // Next, shift by the full set of signatures to hit the respective pubkey NOTE: The data
                // returned is: [<sig0>, <sig1>, ... <sig9>][<pubkey0>, <pubkey1>, ... <pubkey9>]
                var pubStart = n * compressedPubLength + PUBKEYS_OFFSET;
                var pubEnd = (n + 1) * compressedPubLength + PUBKEYS_OFFSET;
                pubkeys.push(res.slice(pubStart, pubEnd));
                // Update offset to hit the next signature slot
                off += DERLength;
                n += 1;
            }
            // Build the transaction data to be serialized
            var preSerializedData = {
                inputs: [],
                outputs: [],
            };
            // First output comes from request dta
            preSerializedData.outputs.push({
                value: req.origData.value,
                recipient: req.origData.recipient,
            });
            if (req.changeData.value > 0) {
                // Second output comes from change data
                preSerializedData.outputs.push({
                    value: req.changeData.value,
                    recipient: changeRecipient,
                });
            }
            // Add the inputs
            for (var i = 0; i < sigs.length; i++) {
                preSerializedData.inputs.push({
                    hash: req.origData.prevOuts[i].txHash,
                    index: req.origData.prevOuts[i].index,
                    sig: sigs[i],
                    pubkey: pubkeys[i],
                    signerPath: req.origData.prevOuts[i].signerPath,
                });
            }
            // Finally, serialize the transaction
            var serializedTx = bitcoin_1.default.serializeTx(preSerializedData);
            // Generate the transaction hash so the user can look this transaction up later
            var preImageTxHash = serializedTx;
            var txHashPre = Buffer.from((0, sha_1.sha256)().update(Buffer.from(preImageTxHash, 'hex')).digest('hex'), 'hex');
            // Add extra data for debugging/lookup purposes
            return {
                tx: serializedTx,
                txHash: (0, sha_1.sha256)().update(txHashPre).digest('hex'),
                changeRecipient: changeRecipient,
                sigs: sigs,
            };
        }
        else if (currencyType === 'ETH') {
            var sig = (0, util_1.parseDER)(res.slice(off, off + 2 + res[off + 1]));
            off += DERLength;
            var ethAddr = res.slice(off, off + 20);
            // Determine the `v` param and add it to the sig before returning
            var _a = ethereum_1.default.buildEthRawTx(req, sig, ethAddr), rawTx = _a.rawTx, sigWithV = _a.sigWithV;
            return {
                tx: "0x".concat(rawTx),
                txHash: "0x".concat(ethereum_1.default.hashTransaction(rawTx)),
                sig: {
                    v: sigWithV.v,
                    r: sigWithV.r.toString('hex'),
                    s: sigWithV.s.toString('hex'),
                },
                signer: ethAddr,
            };
        }
        else if (currencyType === 'ETH_MSG') {
            var sig = (0, util_1.parseDER)(res.slice(off, off + 2 + res[off + 1]));
            off += DERLength;
            var signer = res.slice(off, off + 20);
            var validatedSig = ethereum_1.default.validateEthereumMsgResponse({ signer: signer, sig: sig }, req);
            return {
                sig: {
                    v: validatedSig.v,
                    r: validatedSig.r.toString('hex'),
                    s: validatedSig.s.toString('hex'),
                },
                signer: signer,
            };
        }
        else {
            // Generic signing request
            return (0, genericSigning_1.parseGenericSigningResponse)(res, off, req);
        }
    };
    /**
     * Reset the active wallets to empty values.
     * @category Device Response
     * @internal
     */
    Client.prototype._resetActiveWallets = function () {
        this.activeWallets.internal.uid = EMPTY_WALLET_UID;
        this.activeWallets.internal.name = null;
        this.activeWallets.internal.capabilities = null;
        this.activeWallets.external.uid = EMPTY_WALLET_UID;
        this.activeWallets.external.name = null;
        this.activeWallets.external.capabilities = null;
        return;
    };
    /**
     * Update the payload with the current wallet UID. Some (not all) requests require the active
     * wallet UID in order for Lattice firmware to accept them. If we get a "wrong wallet" error, the
     * SDK will automatically request the current wallet UID from the device and may retry the
     * request, but the original request payload must be modified.
     */
    Client.prototype._replaceWalletUID = function (encReqCode, payload) {
        var wallet = this.getActiveWallet();
        if (!wallet) {
            return null;
        }
        // See if we can modify the payload and retry
        if (encReqCode === 'GET_ADDRESSES') {
            wallet.uid.copy(payload, 0);
            return payload;
        }
        else if (encReqCode === 'SIGN_TRANSACTION') {
            wallet.uid.copy(payload, 2);
            return payload;
        }
        // Not allowed to retry
        return null;
    };
    /**
     * Return JSON-stringified version of state data. Can be used to rehydrate an SDK session without
     * reconnecting to the target Lattice.
     */
    Client.prototype._packStateData = function () {
        try {
            var data = {
                activeWallets: {
                    internal: {
                        uid: this.activeWallets.internal.uid.toString('hex'),
                        name: this.activeWallets.internal.name.toString(),
                        capabilities: this.activeWallets.internal.capabilities,
                    },
                    external: {
                        uid: this.activeWallets.external.uid.toString('hex'),
                        name: this.activeWallets.external.name.toString(),
                        capabilities: this.activeWallets.external.capabilities,
                    },
                },
                ephemeralPub: this.ephemeralPub.getPublic().encode('hex'),
                fwVersion: this.fwVersion.toString('hex'),
                deviceId: this.deviceId,
                name: this.name,
                baseUrl: this.baseUrl,
                privKey: this.privKey.toString('hex'),
                retryCount: this.retryCount,
                timeout: this.timeout,
            };
            return JSON.stringify(data);
        }
        catch (err) {
            console.warn('Could not pack state data.');
            return null;
        }
    };
    /**
     * Unpack a JSON-stringified version of state data and apply it to state. This will allow us to
     * rehydrate an old session.
     */
    Client.prototype._unpackAndApplyStateData = function (data) {
        try {
            var unpacked = JSON.parse(data);
            // Attempty to parse the data
            var internalWallet = {
                uid: Buffer.from(unpacked.activeWallets.internal.uid, 'hex'),
                name: Buffer.from(unpacked.activeWallets.internal.name),
                capabilities: unpacked.activeWallets.internal.capabilities,
                external: false,
            };
            var externalWallet = {
                uid: Buffer.from(unpacked.activeWallets.external.uid, 'hex'),
                name: Buffer.from(unpacked.activeWallets.external.name),
                capabilities: unpacked.activeWallets.external.capabilities,
                external: true,
            };
            var ephemeralPubBytes = Buffer.from(unpacked.ephemeralPub, 'hex');
            var fwVersionBytes = Buffer.from(unpacked.fwVersion, 'hex');
            var privKeyBytes = Buffer.from(unpacked.privKey, 'hex');
            // Apply unpacked params
            this.activeWallets.internal = internalWallet;
            this.activeWallets.external = externalWallet;
            this.ephemeralPub = (0, util_1.getP256KeyPairFromPub)(ephemeralPubBytes);
            this.fwVersion = fwVersionBytes;
            this.deviceId = unpacked.deviceId;
            this.name = unpacked.name;
            this.baseUrl = unpacked.baseUrl;
            this.privKey = privKeyBytes;
            this.key = (0, util_1.getP256KeyPair)(this.privKey);
            this.retryCount = unpacked.retryCount;
            this.timeout = unpacked.timeout;
        }
        catch (err) {
            console.warn('Could not apply state data.');
        }
    };
    // Determine if a provided firmware version matches or exceeds the current firmware version
    Client.prototype._fwVersionGTE = function (_major, _minor, _fix) {
        var _a = this.getFwVersion(), major = _a.major, minor = _a.minor, fix = _a.fix;
        return (major > _major ||
            (major >= _major && minor > _minor) ||
            (major >= _major && minor >= _minor && fix >= _fix));
    };
    /**
     * Get the active wallet.
     * @returns The active wallet.
     */
    Client.prototype.getActiveWallet = function () {
        if (!EMPTY_WALLET_UID.equals(this.activeWallets.external.uid)) {
            return this.activeWallets.external;
        }
        else if (!EMPTY_WALLET_UID.equals(this.activeWallets.internal.uid)) {
            return this.activeWallets.internal;
        }
        else {
            return null;
        }
    };
    /**
     * Check if the user has an active wallet"
     * @returns true is user has active wallet
     */
    Client.prototype.hasActiveWallet = function () {
        return this.getActiveWallet() !== null;
    };
    /**
     * Get 64 bytes representing the public key This is the uncompressed key without the leading 04
     * byte
     * @param LE - Whether to return the public key in little endian format.
     * @returns A Buffer containing the public key.
     */
    Client.prototype.pubKeyBytes = function (LE) {
        if (LE === void 0) { LE = false; }
        var k = this.key.getPublic();
        var p = k.encode('hex');
        var pb = Buffer.from(p, 'hex');
        if (LE === true) {
            // Need to flip X and Y components to little endian
            var x = pb.slice(1, 33).reverse();
            var y = pb.slice(33, 65).reverse();
            // @ts-expect-error - TODO: Find out why Buffer won't accept pb[0]
            return Buffer.concat([pb[0], x, y]);
        }
        else {
            return pb;
        }
    };
    return Client;
}());
exports.Client = Client;
